import { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { notify } from "@/lib/notify";

/**
 * ProactiveCockpitAssistant — Doctrine 3Q appliquée à l'onboarding.
 *
 * V23.15.34 — Objectif : coût cognitif quasi-nul pour l'utilisateur.
 *  - Q1 « Et alors ? »     : "Tu utilises 5 modules, 30 sont dormants. Je peux affiner."
 *  - Q2 « Pourquoi ? »     : usage analysé sur les 30 derniers jours
 *  - Q3 « Que fais-je ? »  : bouton "Configurer en 30s" → 3 questions → cockpit auto
 *
 * Comportement :
 *  - Utilisateur nouveau (< 3 modules utilisés) → propose auto-configuration
 *  - Utilisateur existant → propose affinage périodique
 *  - Bouton "Configurer via IA" ouvre un wizard 3 questions
 *  - Persistance de la fermeture dans localStorage (pas d'insistance)
 */
export default function ProactiveCockpitAssistant() {
  const [suggestion, setSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    try {
      const key = "noam_cockpit_assistant_dismissed";
      const last = localStorage.getItem(key);
      // Ré-affiche après 7 jours
      if (last && Date.now() - parseInt(last, 10) < 7 * 24 * 3600 * 1000) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/onboarding/cockpit-suggestion");
        if (!cancelled) setSuggestion(r.data);
      } catch {
        /* silencieux */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // V23.15.35 — Ouvre le wizard depuis n'importe où (ex: action NOAM "configure_cockpit")
  useEffect(() => {
    const openWizard = () => {
      setDismissed(false);
      setWizardOpen(true);
    };
    window.addEventListener("noam:open-cockpit-wizard", openWizard);
    return () => window.removeEventListener("noam:open-cockpit-wizard", openWizard);
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem("noam_cockpit_assistant_dismissed", String(Date.now()));
    } catch { /* ignore */ }
    setDismissed(true);
  };

  if (dismissed || !suggestion || !suggestion.show_banner) {
    // V23.15.35 — Même en dismissed, on rend le wizard s'il est demandé
    if (wizardOpen) {
      return (
        <CockpitWizard
          onClose={() => setWizardOpen(false)}
          onDone={() => {
            setWizardOpen(false);
            handleDismiss();
            notify.success("Modules reconfigurés selon tes réponses.");
            setTimeout(() => window.location.reload(), 1500);
          }}
        />
      );
    }
    return null;
  }

  return (
    <>
      <div
        className="rounded-xl border p-4 relative"
        style={{
          background: "linear-gradient(135deg, rgba(127,119,221,0.10) 0%, rgba(239,159,39,0.05) 100%)",
          borderColor: "rgba(127,119,221,0.35)",
        }}
        data-testid="cockpit-assistant-banner"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 w-6 h-6 rounded-md hover:bg-white/5 flex items-center justify-center text-zinc-500 hover:text-zinc-300"
          title="Fermer (revient dans 7 jours)"
          data-testid="cockpit-assistant-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-lg bg-[#7F77DD]/20 border border-[#7F77DD]/40 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-[#7F77DD]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#7F77DD] font-semibold">
              Noam · Assistant de configuration
            </div>
            <div className="text-base font-semibold text-white mt-0.5" data-testid="cockpit-assistant-title">
              {suggestion.title}
            </div>
            <div className="text-sm text-zinc-300 mt-1 leading-relaxed" data-testid="cockpit-assistant-narrative">
              {suggestion.narrative}
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setWizardOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium bg-[#EF9F27] text-black hover:bg-[#FFB627] transition-colors"
                data-testid="cockpit-assistant-configure-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Configurer via IA (30 secondes)
                <ArrowRight className="w-3 h-3" />
              </button>
              <span className="text-[11px] text-zinc-500">
                {suggestion.active_modules} module(s) actif(s) · {suggestion.dormant_modules} dormant(s)
              </span>
            </div>
          </div>
        </div>
      </div>
      {wizardOpen && (
        <CockpitWizard
          onClose={() => setWizardOpen(false)}
          onDone={() => {
            setWizardOpen(false);
            handleDismiss();
            notify.success("Modules reconfigurés selon tes réponses.");
            setTimeout(() => window.location.reload(), 1500);
          }}
        />
      )}
    </>
  );
}

/**
 * CockpitWizard — 3 questions minimales, 1 action.
 */
function CockpitWizard({ onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ urgency: "", volume: "", horizon: "" });
  const [saving, setSaving] = useState(false);

  const questions = [
    {
      key: "urgency",
      title: "Quelle est ta priorité absolue en ce moment ?",
      options: [
        { value: "risques", label: "Piloter mes risques", hint: "Contrôles, conformité, incidents" },
        { value: "croissance", label: "Générer de la croissance", hint: "Pipeline, deals, contacts" },
        { value: "cartographie", label: "Comprendre mon écosystème", hint: "Dépendances, fractales, ontologie" },
        { value: "surveillance", label: "Détecter les signaux faibles", hint: "Radar, veille, alertes" },
      ],
    },
    {
      key: "volume",
      title: "Combien d'éléments actifs gères-tu au quotidien ?",
      options: [
        { value: "petit", label: "< 20", hint: "Vue épurée, focus" },
        { value: "moyen", label: "20-100", hint: "Configuration standard" },
        { value: "gros", label: "> 100", hint: "Filtres avancés, groupes" },
      ],
    },
    {
      key: "horizon",
      title: "Sur quel horizon décides-tu principalement ?",
      options: [
        { value: "operationnel", label: "Quotidien / hebdo", hint: "Actions, incidents" },
        { value: "tactique", label: "Mensuel / trimestriel", hint: "Plans, deals, revues" },
        { value: "strategique", label: "Annuel / pluriannuel", hint: "Doctrine 51, fractales, capital" },
      ],
    },
  ];

  const current = questions[step];
  const total = questions.length;

  const apply = async () => {
    setSaving(true);
    try {
      await api.post("/workspace/onboarding/apply-cockpit-suggestion", answers);
      onDone();
    } catch (e) {
      notify.error("Impossible d'appliquer la configuration. Réessaie.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cockpit-wizard-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="noam-card w-full max-w-lg p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="cockpit-wizard"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-md hover:bg-white/5 flex items-center justify-center text-zinc-400"
        >
          <X className="w-4 h-4" />
        </button>
        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-4">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "bg-[#EF9F27] w-8" : i < step ? "bg-[#1D9E75] w-4" : "bg-zinc-700 w-4"}`}
            />
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#7F77DD] font-semibold">
          Question {step + 1} / {total}
        </div>
        <h2 className="text-lg font-semibold text-white mt-1">{current.title}</h2>
        <div className="mt-4 space-y-2" data-testid={`cockpit-wizard-q${step}`}>
          {current.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                const next = { ...answers, [current.key]: opt.value };
                setAnswers(next);
                if (step < total - 1) setStep(step + 1);
              }}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                answers[current.key] === opt.value
                  ? "border-[#EF9F27] bg-[#EF9F27]/10"
                  : "border-white/10 hover:border-white/20 hover:bg-white/5"
              }`}
              data-testid={`cockpit-wizard-option-${opt.value}`}
            >
              <div className="flex items-center gap-2">
                {answers[current.key] === opt.value && <CheckCircle2 className="w-4 h-4 text-[#EF9F27]" />}
                <span className="text-sm font-medium text-white">{opt.label}</span>
              </div>
              <div className="text-xs text-zinc-400 mt-1 ml-6">{opt.hint}</div>
            </button>
          ))}
        </div>
        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="text-xs px-3 py-1.5 rounded-md text-zinc-400 hover:text-white disabled:opacity-30"
          >
            ← Retour
          </button>
          {step === total - 1 && answers[current.key] && (
            <button
              onClick={apply}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md font-medium bg-[#EF9F27] text-black hover:bg-[#FFB627] disabled:opacity-50 transition-colors"
              data-testid="cockpit-wizard-apply"
            >
              {saving ? "Configuration…" : "Appliquer ma configuration"}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
